/**
 * CorrelationComments — Reddit-style threaded comments on a public correlation.
 *
 * Lives inside the CorrelationModal (so reading a correlation in /weekly or
 * from /settings#you exposes the conversation right there).
 *
 * Capabilities:
 *  - Top + New sort
 *  - Upvote-only "Resonates" with live score
 *  - Threading nested up to 4 levels visible (after that, a "continue thread"
 *    affordance keeps things from running off the page)
 *  - Collapse/expand any subtree
 *  - Permalink — copy the comment URL to clipboard; if the modal opens with
 *    location.hash matching #comment-<id>, that node is scrolled into view
 *    and pulsed
 *  - Soft-delete by owner
 *
 * The backend lives at /app/backend/routers/correlation_comments.py.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Trash2, CornerDownRight, MessageCircle, Link2, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const VISIBLE_DEPTH = 4;       // beyond this we render a "continue thread" hint
const MAX_LEN = 2000;

// Relative-time formatter used in every row.
function when(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 14) return `${d}d`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}

export default function CorrelationComments({ correlationId, onCountChange }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState("top");   // "top" | "new"
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Distinct from `collapsed`: lists comment ids whose kids the user has
  // explicitly chosen to reveal past the visible-depth ceiling.
  const [continueExpanded, setContinueExpanded] = useState(() => new Set());
  const [replyTo, setReplyTo] = useState(null);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // "New since last visit" split. `lastVisit` is null on first visit or when
  // signed-out; in that case nothing is treated as "old" and everything stays
  // expanded. Roots are considered "new" if the root itself OR any descendant
  // was posted after `lastVisit`.
  const [lastVisit, setLastVisit] = useState(null);
  const [revealOlder, setRevealOlder] = useState(false);
  const listRef = useRef(null);

  // --- load -----------------------------------------------------------------

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/correlations/${correlationId}/comments`,
        { params: { sort } },
      );
      setItems(data || []);
    } catch (err) {
      console.warn("[CorrelationComments] load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [correlationId, sort]);

  useEffect(() => { if (correlationId) load(); }, [correlationId, load]);

  // On mount (authed only): read the previous last-visit cutoff so we can
  // split comments into "new since" vs "already read", then immediately mark
  // NOW as the new visit so the next reload picks up the fresh cutoff.
  useEffect(() => {
    if (!user || !correlationId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/correlations/${correlationId}/last-visit`);
        if (!cancelled) setLastVisit(data?.last_visit_at || null);
      } catch { /* silent — feature degrades to "no split" */ }
      try {
        await api.post(`/correlations/${correlationId}/mark-visit`);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user, correlationId]);

  // After items render, honour any #comment-<id> in the URL: scroll + pulse.
  useEffect(() => {
    if (loading || items.length === 0) return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash.startsWith("comment-")) return;
    const id = hash.slice(8);
    if (!items.some((c) => c.id === id)) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById(`comment-${id}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("weekly-deeplink-pulse");
      window.setTimeout(() => el.classList.remove("weekly-deeplink-pulse"), 2200);
    }, 60);
    return () => window.clearTimeout(t);
  }, [items, loading]);

  // --- tree building --------------------------------------------------------

  // Group children by parent_id once; assemble the tree in render.
  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const c of items) {
      const k = c.parent_id || "__root__";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    }
    return map;
  }, [items]);

  const roots = childrenByParent.get("__root__") || [];

  // "New since last visit" partitioning. A root is considered "new" if it or
  // any descendant was posted after `lastVisit`. When signed-out or on first
  // visit, we treat everything as new (no fold) so nothing is hidden.
  const isRootNew = useMemo(() => {
    if (!lastVisit) return () => true;
    const cutoff = new Date(lastVisit).getTime();
    if (Number.isNaN(cutoff)) return () => true;
    const memo = new Map();
    const nodesById = new Map(items.map((c) => [c.id, c]));
    function walk(id) {
      if (memo.has(id)) return memo.get(id);
      const c = nodesById.get(id);
      if (!c) { memo.set(id, false); return false; }
      const own = new Date(c.created_at).getTime() > cutoff;
      if (own) { memo.set(id, true); return true; }
      const kids = childrenByParent.get(id) || [];
      const any = kids.some((k) => walk(k.id));
      memo.set(id, any);
      return any;
    }
    return walk;
  }, [lastVisit, items, childrenByParent]);

  // Per-node "new since last visit" — used to add a soft gold accent.
  const isNodeNew = (c) => {
    if (!lastVisit || !c?.created_at) return false;
    const cutoff = new Date(lastVisit).getTime();
    if (Number.isNaN(cutoff)) return false;
    return new Date(c.created_at).getTime() > cutoff;
  };

  const { newRoots, oldRoots } = useMemo(() => {
    if (!lastVisit) return { newRoots: roots, oldRoots: [] };
    const n = [], o = [];
    for (const r of roots) {
      if (isRootNew(r.id)) n.push(r);
      else o.push(r);
    }
    return { newRoots: n, oldRoots: o };
  }, [roots, lastVisit, isRootNew]);

  // --- actions --------------------------------------------------------------

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to join the conversation."); return; }
    const body = content.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const payload = { correlation_id: correlationId, content: body };
      if (replyTo) payload.parent_id = replyTo.id;
      const { data } = await api.post(`/correlations/${correlationId}/comments`, payload);
      setItems((cur) => [...cur, data]);
      setContent("");
      setReplyTo(null);
      onCountChange?.(+1);
      toast.success(replyTo ? "Reply posted." : "Comment posted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/correlations/comments/${id}`);
      // Hard-delete: strip the comment AND every descendant reply from local
      // state so nothing lingers in the thread.
      setItems((cur) => {
        const doomed = new Set([id]);
        let grew = true;
        while (grew) {
          grew = false;
          for (const c of cur) {
            if (c.parent_id && doomed.has(c.parent_id) && !doomed.has(c.id)) {
              doomed.add(c.id);
              grew = true;
            }
          }
        }
        return cur.filter((c) => !doomed.has(c.id));
      });
      const removed = Number(data?.deleted) || 1;
      onCountChange?.(-removed);
    } catch {
      toast.error("Could not remove.");
    }
  };

  // Optimistic toggle — bumps the local score immediately, reverts on error.
  const toggleResonate = async (id) => {
    if (!user) { toast.error("Sign in to resonate."); return; }
    let snapshot = null;
    setItems((cur) => cur.map((c) => {
      if (c.id !== id) return c;
      snapshot = c;
      const next = !c.my_resonate;
      return { ...c, my_resonate: next, score: (c.score || 0) + (next ? 1 : -1) };
    }));
    try {
      const { data } = await api.post(`/correlations/comments/${id}/resonate`);
      // Reconcile with server-truth (handles double-clicks / races).
      setItems((cur) => cur.map((c) => c.id === id ? { ...c, score: data.score, my_resonate: data.my_resonate } : c));
    } catch (err) {
      // Revert on failure.
      if (snapshot) setItems((cur) => cur.map((c) => c.id === id ? snapshot : c));
      toast.error(err.response?.data?.detail || "Could not save.");
    }
  };

  const toggleCollapse = (id) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const revealBeyondDepth = (id) => {
    setContinueExpanded((cur) => {
      if (cur.has(id)) return cur;
      const next = new Set(cur);
      next.add(id);
      return next;
    });
  };

  const copyPermalink = async (id) => {
    const url = `${window.location.origin}/weekly#comment-${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Comment link copied.");
    } catch {
      toast.error("Could not copy link.");
    }
  };

  // --- render ---------------------------------------------------------------

  const total = items.length;

  return (
    <section
      data-testid="correlation-comments"
      className="mt-10 pt-8 hairline-t"
    >
      <header className="flex items-center gap-3 flex-wrap mb-5">
        <MessageCircle size={14} className="text-[hsl(var(--accent))]" />
        <span className="label">Comments</span>
        <span
          data-testid="correlation-comments-total"
          className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
        >
          {total}
        </span>
        <div className="ml-auto inline-flex items-center border border-border rounded-full overflow-hidden bg-secondary/30">
          {[
            { id: "top", label: "Top" },
            { id: "new", label: "New" },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`comments-sort-${s.id}`}
              onClick={() => setSort(s.id)}
              aria-pressed={sort === s.id}
              className={`mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 transition-colors ${
                sort === s.id
                  ? "bg-background text-[hsl(var(--primary))]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </header>

      {/* COMPOSER (root reply) */}
      <Composer
        user={user}
        replyTo={replyTo}
        onCancelReply={() => { setReplyTo(null); setContent(""); }}
        content={content}
        setContent={setContent}
        submitting={submitting}
        onSubmit={submit}
        // Show the root composer ONLY when not replying to a specific comment
        // (replies use an inline composer rendered below the target row).
        hidden={!!replyTo}
      />

      {/* LIST */}
      <div ref={listRef}>
        {loading ? (
          <p className="serif italic text-muted-foreground mt-4">Loading…</p>
        ) : roots.length === 0 ? (
          <p className="serif italic text-muted-foreground mt-4">
            Be the first to add a thread.
          </p>
        ) : (
          <>
            {oldRoots.length > 0 && newRoots.length > 0 && (
              <div className="mb-3 mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                New since your last visit
              </div>
            )}
            <ul className="space-y-5">
              {newRoots.map((c) => (
                <CommentNode
                  key={c.id}
                  comment={c}
                  childrenByParent={childrenByParent}
                  collapsed={collapsed}
                  onToggleCollapse={toggleCollapse}
                  continueExpanded={continueExpanded}
                  onRevealBeyondDepth={revealBeyondDepth}
                  onResonate={toggleResonate}
                  onReply={(target) => { setReplyTo(target); setContent(""); }}
                  onRemove={remove}
                  onCopyPermalink={copyPermalink}
                  currentUserId={user?.user_id}
                  replyTo={replyTo}
                  replyContent={content}
                  setReplyContent={setContent}
                  onSubmitReply={submit}
                  onCancelReply={() => { setReplyTo(null); setContent(""); }}
                  submitting={submitting}
                  user={user}
                  isNodeNew={isNodeNew}
                />
              ))}
            </ul>
            {oldRoots.length > 0 && (
              <div className="mt-6">
                {!revealOlder ? (
                  <button
                    type="button"
                    data-testid="comments-reveal-older"
                    onClick={() => setRevealOlder(true)}
                    className="mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 hairline text-muted-foreground hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))] transition-colors inline-flex items-center gap-2"
                  >
                    <ChevronRight size={11} />
                    {oldRoots.length} previous thread{oldRoots.length === 1 ? "" : "s"} — expand
                  </button>
                ) : (
                  <>
                    <div className="mb-3 flex items-center gap-3">
                      <span className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Previously read
                      </span>
                      <button
                        type="button"
                        data-testid="comments-hide-older"
                        onClick={() => setRevealOlder(false)}
                        className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-[hsl(var(--primary))] underline"
                      >
                        collapse
                      </button>
                    </div>
                    <ul className="space-y-5 opacity-80">
                      {oldRoots.map((c) => (
                        <CommentNode
                          key={c.id}
                          comment={c}
                          childrenByParent={childrenByParent}
                          collapsed={collapsed}
                          onToggleCollapse={toggleCollapse}
                          continueExpanded={continueExpanded}
                          onRevealBeyondDepth={revealBeyondDepth}
                          onResonate={toggleResonate}
                          onReply={(target) => { setReplyTo(target); setContent(""); }}
                          onRemove={remove}
                          onCopyPermalink={copyPermalink}
                          currentUserId={user?.user_id}
                          replyTo={replyTo}
                          replyContent={content}
                          setReplyContent={setContent}
                          onSubmitReply={submit}
                          onCancelReply={() => { setReplyTo(null); setContent(""); }}
                          submitting={submitting}
                          user={user}
                          isNodeNew={isNodeNew}
                        />
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* COMPOSER                                                                   */
/* -------------------------------------------------------------------------- */

function Composer({ user, replyTo, onCancelReply, content, setContent, submitting, onSubmit, hidden, inline, autoFocus }) {
  if (hidden) return null;
  return (
    <form
      onSubmit={onSubmit}
      data-testid={inline ? "comments-composer-inline" : "comments-composer"}
      className={`paper p-4 ${inline ? "mt-3" : "mb-7"}`}
    >
      {replyTo && (
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-2">
          <CornerDownRight size={11} />
          Replying to {replyTo.user_name}
          <button
            type="button"
            data-testid="comments-cancel-reply"
            onClick={onCancelReply}
            className="ml-2 underline hover:text-foreground"
          >
            cancel
          </button>
        </div>
      )}
      <textarea
        data-testid={inline ? "comments-input-inline" : "comments-input"}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={2}
        maxLength={MAX_LEN}
        placeholder={user ? (replyTo ? "Write a reply…" : "Add a comment…") : "Sign in to join the conversation."}
        disabled={!user}
        autoFocus={!!autoFocus}
        className="input-line w-full text-[15px] leading-relaxed resize-none disabled:opacity-60"
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {content.length}/{MAX_LEN}
        </span>
        <button
          type="submit"
          data-testid={inline ? "comments-submit-inline" : "comments-submit"}
          disabled={submitting || !content.trim() || !user}
          className="btn-gold disabled:opacity-40"
        >
          {submitting ? "Posting…" : (replyTo ? "Reply" : "Post")}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* COMMENT NODE (recursive)                                                   */
/* -------------------------------------------------------------------------- */

function CommentNode({
  comment, childrenByParent, collapsed, onToggleCollapse,
  continueExpanded, onRevealBeyondDepth,
  onResonate, onReply, onRemove, onCopyPermalink, currentUserId,
  replyTo, replyContent, setReplyContent, onSubmitReply, onCancelReply,
  submitting, user, isNodeNew,
}) {
  const id = comment.id;
  const kids = childrenByParent.get(id) || [];
  const isCollapsed = collapsed.has(id);
  const isMine = currentUserId && comment.user_id === currentUserId;
  const showInlineComposer = replyTo && replyTo.id === id;
  const depth = comment.depth || 0;
  // At max visible depth, instead of recursing further visually we surface a
  // "continue thread →" affordance that scrolls to the same comment in its own
  // permalink page. (We don't have a dedicated page; we just expand the rest
  // inline at the same indentation, which keeps things scannable.)
  const overflow = depth + 1 > VISIBLE_DEPTH;
  const expandedPastDepth = continueExpanded.has(id);
  const isNew = isNodeNew ? isNodeNew(comment) : false;

  return (
    <li
      id={`comment-${id}`}
      data-testid={`comment-${id}`}
      className={`relative ${isNew ? "border-l-2 border-[hsl(var(--primary))] pl-3 -ml-3" : ""}`}
    >
      <div className="flex gap-3">
        {/* Avatar — clickable to profile */}
        <Link
          to={`/u/${comment.user_handle || comment.user_id}`}
          data-testid={`comment-avatar-${id}`}
          aria-label={`Open ${comment.user_name || "reader"}'s profile`}
          className="flex-shrink-0 mt-0.5"
        >
          {comment.user_picture
            ? <img src={comment.user_picture} alt="" className="w-7 h-7 rounded-full" />
            : <div className="w-7 h-7 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-xs">
                {(comment.user_name || "?")[0]?.toUpperCase()}
              </div>}
        </Link>

        <div className="flex-1 min-w-0">
          {/* Meta */}
          <div className="flex items-baseline gap-2.5 flex-wrap mb-1">
            <Link
              to={`/u/${comment.user_handle || comment.user_id}`}
              className="serif text-[15px] text-foreground hover:text-[hsl(var(--primary))] transition-colors"
            >
              {comment.user_name}
            </Link>
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
              {when(comment.created_at)}
            </span>
            {(comment.score || 0) !== 0 && (
              <span
                className="mono text-[10px] tabular-nums text-muted-foreground/80"
                data-testid={`comment-score-${id}`}
              >
                {comment.score} pt{comment.score === 1 ? "" : "s"}
              </span>
            )}
            {kids.length > 0 && (
              <button
                type="button"
                onClick={() => onToggleCollapse(id)}
                data-testid={`comment-collapse-${id}`}
                aria-label={isCollapsed ? "Expand thread" : "Collapse thread"}
                className="ml-auto mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--primary))] inline-flex items-center gap-1"
              >
                {isCollapsed
                  ? <><ChevronRight size={11} /> {kids.length}</>
                  : <><ChevronDown size={11} /></>}
              </button>
            )}
          </div>

          {/* Body */}
          <p className="text-foreground/90 text-[15px] leading-relaxed whitespace-pre-wrap">
            {comment.content}
          </p>

          {/* Action row */}
          {!isCollapsed && (
            <div className="mt-2 flex items-center gap-3 flex-wrap text-muted-foreground">
              <button
                type="button"
                onClick={() => onResonate(id)}
                data-testid={`comment-resonate-${id}`}
                aria-pressed={!!comment.my_resonate}
                className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  comment.my_resonate ? "text-[hsl(var(--primary))]" : "hover:text-[hsl(var(--primary))]"
                }`}
                title={comment.my_resonate ? "Remove resonate" : "Resonates with me"}
              >
                <ArrowUp size={12} strokeWidth={2.25} />
                <span className="tabular-nums">{comment.score || 0}</span>
              </button>
              <button
                type="button"
                onClick={() => onReply(comment)}
                data-testid={`comment-reply-${id}`}
                className="mono text-[10px] uppercase tracking-[0.18em] hover:text-[hsl(var(--primary))] transition-colors"
              >
                Reply
              </button>
              <button
                type="button"
                onClick={() => onCopyPermalink(id)}
                data-testid={`comment-permalink-${id}`}
                title="Copy comment link"
                className="mono text-[10px] uppercase tracking-[0.18em] hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center gap-1"
              >
                <Link2 size={10} />
                Permalink
              </button>
              {isMine && (
                <button
                  type="button"
                  onClick={() => onRemove(id)}
                  data-testid={`comment-delete-${id}`}
                  className="mono text-[10px] uppercase tracking-[0.18em] hover:text-[hsl(var(--destructive))] transition-colors inline-flex items-center gap-1"
                >
                  <Trash2 size={10} /> Remove
                </button>
              )}
            </div>
          )}

          {/* Inline reply composer (only when the user clicked Reply on THIS row) */}
          {showInlineComposer && (
            <Composer
              user={user}
              replyTo={comment}
              content={replyContent}
              setContent={setReplyContent}
              submitting={submitting}
              onSubmit={onSubmitReply}
              onCancelReply={onCancelReply}
              inline
              autoFocus
            />
          )}

          {/* CHILDREN */}
          {kids.length > 0 && !isCollapsed && (
            overflow && !expandedPastDepth ? (
              <p className="mt-3 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/85">
                <button
                  type="button"
                  data-testid={`comment-continue-${id}`}
                  onClick={() => onRevealBeyondDepth(id)}
                  className="hover:text-[hsl(var(--primary))]"
                >
                  Continue thread ({kids.length}) →
                </button>
              </p>
            ) : (
              <ul className="mt-4 pl-4 border-l border-border space-y-5">
                {kids.map((k) => (
                  <CommentNode
                    key={k.id}
                    comment={k}
                    childrenByParent={childrenByParent}
                    collapsed={collapsed}
                    onToggleCollapse={onToggleCollapse}
                    continueExpanded={continueExpanded}
                    onRevealBeyondDepth={onRevealBeyondDepth}
                    onResonate={onResonate}
                    onReply={onReply}
                    onRemove={onRemove}
                    onCopyPermalink={onCopyPermalink}
                    currentUserId={currentUserId}
                    replyTo={replyTo}
                    replyContent={replyContent}
                    setReplyContent={setReplyContent}
                    onSubmitReply={onSubmitReply}
                    onCancelReply={onCancelReply}
                    submitting={submitting}
                    user={user}
                    isNodeNew={isNodeNew}
                  />
                ))}
              </ul>
            )
          )}
        </div>
      </div>
    </li>
  );
}
